import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";

const CATS = [
  { slug: "all", name: "All creations" },
  { slug: "bags", name: "Bags" },
  { slug: "plushies", name: "Plushies" },
  { slug: "wearables", name: "Wearables" },
];

export default function Shop() {
  const { collection } = useParams();
  const [params, setParams] = useSearchParams();
  const category = collection || params.get("c") || "all";
  const [search, setSearch] = useState(params.get("q") || "");
  const [sort, setSort] = useState("newest");
  const [products, setProducts] = useState(null);

  useEffect(() => {
    const q = new URLSearchParams();
    if (category !== "all") q.set("category", category);
    if (search) q.set("search", search);
    q.set("sort", sort);
    setProducts(null);
    api(`/products?${q}`).then(setProducts).catch(() => setProducts([]));
  }, [category, search, sort]);

  const title = CATS.find((c) => c.slug === category)?.name || "Shop";
  const setCategory = (slug) => {
    const next = new URLSearchParams(params);
    next.delete("c");
    if (slug !== "all") next.set("c", slug);
    setParams(next, { replace: true });
  };

  return (
    <div className="bg-candy-mist min-h-screen">
      <Seo title={collection ? `Shop ${title}` : "Shop All Creations"} description="Browse every handmade crochet creation — cloud-soft bags, cuddly plushies and cozy wearables, all hooked by Rachel." path={collection ? `/shop/${collection}` : "/shop"} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <Reveal className="text-center">
          <h1 data-testid="shop-heading" className="font-display font-semibold text-3xl sm:text-4xl">{title}</h1>
          <p className="text-muted font-semibold mt-2">Every piece hooked by hand, one happy stitch at a time.</p>
        </Reveal>

        <div className="mt-8 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="shop-category-pills">
            {CATS.map((c) => (
              <button
                key={c.slug}
                data-testid={`shop-pill-${c.slug}`}
                onClick={() => setCategory(c.slug)}
                className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors duration-200 ${category === c.slug ? "bg-candy-glow border-candy text-candy-deep" : "bg-white border-candy-soft text-ink/70 hover:bg-candy-soft"}`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1 md:w-56">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
              <input
                data-testid="shop-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search creations…"
                aria-label="Search products"
                className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white border border-candy-soft focus:border-candy outline-none font-semibold text-sm"
              />
            </div>
            <select data-testid="shop-sort-select" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort products" className="px-4 py-2.5 rounded-full bg-white border border-candy-soft focus:border-candy outline-none font-bold text-sm">
              <option value="newest">Newest</option>
              <option value="price_asc">Price ↑</option>
              <option value="price_desc">Price ↓</option>
            </select>
          </div>
        </div>

        {products === null ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10" data-testid="shop-loading">
            {[1, 2, 3].map((i) => <div key={i} className="h-96 rounded-cloud bg-white/70 animate-pulse" />)}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20" data-testid="shop-empty-state">
            <p className="text-5xl mb-4" aria-hidden="true">🧶</p>
            <h2 className="font-display font-semibold text-2xl">Nothing here yet!</h2>
            <p className="text-muted mt-2">This collection is still on the hook. Rachel would love to make you something custom.</p>
            <Link to="/custom-orders" data-testid="shop-empty-custom-cta" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-candy text-white font-bold shadow-pink hover:bg-candy-deep transition-colors duration-200">
              Request a custom creation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10" data-testid="shop-grid">
            {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
